import { useEffect, useRef } from "react";
import { Dict } from "mixpanel-browser";
import AnalyticsService from "@/services/analytics";

interface TrackPageViewOptions {
  /**
   * Extra properties forwarded to the Mixpanel event payload.
   * e.g. { AccountId: user.id }
   */
  data?: Dict;
  /**
   * When provided, tracking is deferred until this is true.
   * Useful for authenticated pages that need user data before sending.
   * Defaults to true (fires immediately on mount).
   */
  ready?: boolean;
}

/**
 * Tracks a page-view event (Mixpanel + GA) exactly once per real mount.
 *
 * React 18 StrictMode double-invokes effects in development:
 *   mount → cleanup → remount
 * By scheduling the tracking call inside a setTimeout and cancelling it in
 * the cleanup, StrictMode's synchronous cleanup cancels the first timer so
 * only the surviving mount's timer fires.
 *
 * On real navigation (user leaves and returns), the component truly unmounts
 * so a new instance creates a fresh timer and the event fires again.
 *
 * @example Basic (public page)
 *   useTrackPageView("homePageViewed");
 *
 * @example With data + auth guard (authenticated page)
 *   useTrackPageView("activityPageViewed", {
 *     ready: !isFetching && !!user,
 *     data: { AccountId: user?.id },
 *   });
 */
export function useTrackPageView(key: string, options?: TrackPageViewOptions): void {
  const { data = {}, ready = true } = options ?? {};

  // Keep a stable ref so the timer closure always reads the latest data
  // without needing data as an effect dependency (which would re-fire the event).
  const dataRef = useRef<Dict>(data);
  dataRef.current = data;

  useEffect(() => {
    if (!ready) return;

    let cancelled = false;

    const id = setTimeout(() => {
      if (!cancelled) {
        AnalyticsService.trackGAPagesHandler(key);
        AnalyticsService.trackMx(key, dataRef.current);
      }
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  // Re-runs only when ready flips to true. key is intentionally stable (set on mount).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);
}
